/**
 * PGMQ Mycelium Transport (Supabase-native)
 *
 * Publishes events to a Supabase PGMQ queue instead of GCP Pub/Sub.
 * Eliminates external infrastructure — events stay inside Postgres.
 *
 * Supports:
 * - Priority-based routing (critical/high → separate queue)
 * - Client-native publishing (works from browser via supabase-js)
 * - Transactional publishing (event + data in same Postgres transaction)
 *
 * Usage:
 * ```ts
 * import { PgmqMyceliumTransport } from './mycelium-transport-pgmq';
 * import { setMyceliumTransport } from './mycelium-transport-registry';
 * import { createClient } from '@supabase/supabase-js';
 *
 * const supabase = createClient(url, key);
 * setMyceliumTransport(new PgmqMyceliumTransport({ supabaseClient: supabase }));
 * ```
 *
 * @module mycelium-transport-pgmq
 * @since 2026-03-04
 * @see docs/protocols/mycelium/PGMQ_TRANSFORMATION_RESEARCH.md
 */
import type { IMyceliumTransport, MyceliumEventPayload } from "./mycelium-transport.types";
import { createServiceError, } from "./service-registry";
import { logger } from "../lib/logger";
// ============================================================================
// TYPES
// ============================================================================
/** Configuration for the PGMQ transport */
export interface PgmqTransportConfig {
    /**
     * Supabase client instance. Must support .schema('pgmq_public').rpc().
     * Can be a browser client (supabase-js) or server client (service role).
     */
    supabaseClient: PgmqSupabaseClient;
    /**
     * Default queue name for normal-priority events.
     * @default 'mycelium_events'
     */
    queueName?: string;
    /**
     * Queue name for critical/high-priority events.
     * @default 'mycelium_high_priority'
     */
    highPriorityQueueName?: string;
    /**
     * Whether to use the SQL helper function `mycelium_route_event()`
     * instead of direct pgmq.send(). The SQL function handles priority routing.
     * @default false
     */
    useRouterFunction?: boolean;
}
/**
 * Minimal Supabase client interface for PGMQ operations.
 * Compatible with both browser and server Supabase clients.
 */
export interface PgmqSupabaseClient {
    schema(name: string): {
        rpc(fn: string, params: Record<string, unknown>): Promise<{
            data: unknown;
            error: PgmqError | null;
        }>;
    };
    rpc(fn: string, params: Record<string, unknown>): Promise<{
        data: unknown;
        error: PgmqError | null;
    }>;
}
/** Error shape from Supabase client */
interface PgmqError {
    message: string;
    code?: string;
    details?: string;
}
// ============================================================================
// CONSTANTS
// ============================================================================
const DEFAULT_QUEUE = "mycelium_events";
const HIGH_PRIORITY_QUEUE = "mycelium_high_priority";
const PGMQ_SCHEMA = "pgmq_public";
/** Valid priority levels for event routing */
const HIGH_PRIORITY_VALUES = new Set(["critical", "high"]);
// ============================================================================
// TRANSPORT IMPLEMENTATION
// ============================================================================
/**
 * PGMQ transport for Mycelium events.
 *
 * Publishes events to Supabase PGMQ queues using the pgmq_public schema.
 * Supports priority-based routing: critical/high events go to a separate queue.
 */
export class PgmqMyceliumTransport implements IMyceliumTransport {
    private config: Required<PgmqTransportConfig>;
    constructor(options: PgmqTransportConfig) {
        this.config = {
            supabaseClient: options.supabaseClient,
            queueName: options.queueName ?? DEFAULT_QUEUE,
            highPriorityQueueName: options.highPriorityQueueName ?? HIGH_PRIORITY_QUEUE,
            useRouterFunction: options.useRouterFunction ?? false,
        };
    }
    /**
     * Publish an event to the PGMQ queue.
     * Routes critical/high priority events to a separate queue.
     */
    async publish(event: MyceliumEventPayload) {
        try {
            // Determine priority from payload
            const priority = (event.payload?.priority as string) ?? "normal";
            const isHighPriority = HIGH_PRIORITY_VALUES.has(priority);
            // Route via SQL function or direct pgmq.send
            if (this.config.useRouterFunction) {
                return await this.publishViaRouter(event, priority);
            }
            return await this.publishDirect(event, isHighPriority);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error("PGMQ Mycelium transport: publish failed", {
                domain: event.domain,
                type: event.type,
                error: message,
            });
            return { success: false, error: message, code: "PGMQ_PUBLISH_FAILED", service: "PgmqMyceliumTransport" };
        }
    }
    /**
     * Publish directly via pgmq_public.send() RPC.
     */
    private async publishDirect(event: MyceliumEventPayload, isHighPriority: boolean) {
        const queueName = isHighPriority
            ? this.config.highPriorityQueueName
            : this.config.queueName;
        const message = this.serializeEvent(event);
        const { data, error } = await this.config.supabaseClient
            .schema(PGMQ_SCHEMA)
            .rpc("send", {
            queue_name: queueName,
            message,
        });
        if (error) {
            logger.error("PGMQ Mycelium transport: queue send failed", {
                queue: queueName,
                domain: event.domain,
                type: event.type,
                error: error.message,
            });
            return { success: false, error: error.message, code: "PGMQ_SEND_FAILED", service: "PgmqMyceliumTransport" };
        }
        const messageId = String(data ?? "unknown");
        logger.debug("PGMQ Mycelium transport: event published", {
            queue: queueName,
            domain: event.domain,
            type: event.type,
            messageId,
        });
        return { success: true, data: messageId };
    }
    /**
     * Publish via the mycelium_route_event() SQL function.
     * The function handles priority routing server-side.
     */
    private async publishViaRouter(event: MyceliumEventPayload, priority: string) {
        const { data, error } = await this.config.supabaseClient.rpc("mycelium_route_event", {
            p_domain: event.domain,
            p_type: event.type,
            p_user_id: event.userId ?? null,
            p_org_id: event.orgId ?? null,
            p_team_id: event.teamId ?? null,
            p_payload: event.payload ?? {},
            p_priority: priority,
        });
        if (error) {
            logger.error("PGMQ Mycelium transport: router function failed", {
                domain: event.domain,
                type: event.type,
                error: error.message,
            });
            return { success: false, error: error.message, code: "PGMQ_ROUTER_FAILED", service: "PgmqMyceliumTransport" };
        }
        const messageId = String(data ?? "unknown");
        logger.debug("PGMQ Mycelium transport: event routed", {
            domain: event.domain,
            type: event.type,
            priority,
            messageId,
        });
        return { success: true, data: messageId };
    }
    /**
     * Serialize a MyceliumEventPayload into a PGMQ-compatible JSON message.
     *
     * Validates required fields before serialization to prevent invalid events
     * from entering the queue. Throws descriptive errors for missing required data.
     *
     * @throws Error if required fields (domain, type) are missing
     */
    private serializeEvent(event: MyceliumEventPayload): Record<string, unknown> {
        // Validate required fields to prevent silent data loss
        if (!event.domain || typeof event.domain !== 'string') {
            throw new Error(`MyceliumEvent validation failed: 'domain' is required and must be a string. ` +
                `Received: ${event.domain === undefined ? 'undefined' : typeof event.domain}`);
        }
        if (!event.type || typeof event.type !== 'string') {
            throw new Error(`MyceliumEvent validation failed: 'type' is required and must be a string. ` +
                `Received: ${event.type === undefined ? 'undefined' : typeof event.type}`);
        }
        // Log warning if userId/orgId missing but don't fail - some system events may not have these
        if (!event.userId && !event.orgId) {
            logger.warn('PGMQ Mycelium transport: serializing event without userId or orgId', { domain: event.domain, type: event.type });
        }
        return {
            domain: event.domain,
            type: event.type,
            userId: event.userId ?? null,
            orgId: event.orgId ?? null,
            teamId: event.teamId ?? null,
            payload: event.payload ?? {},
            version: event.version ?? 1,
            // toISOString() returns UTC timestamp - ensures consistent ordering across timezones
            publishedAt: new Date().toISOString(),
        };
    }
}
